// src/features/vendor/api/vendorApi.js
import { apiClient } from '@/lib/apiClient';

const get = async (path, options) => {
  const { data, error } = await apiClient.GET(path, options);
  if (error) throw error;
  return data;
};

export const vendorApi = {
  // Profile
  getProfile: () => get('/api/v1/vendor/profile'),

  updateProfile: async (body) => {
    const { data, error } = await apiClient.PATCH('/api/v1/vendor/profile', { body });
    if (error) throw error;
    return data;
  },

  uploadBanner: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const { data, error } = await apiClient.POST('/api/v1/vendor/profile/banner', {
      body: formData,
    });
    if (error) throw error;
    return data;
  },

  // Products
  getProducts: async () => {
    const products = [];
    const size = 100;
    let page = 1;

    while (true) {
      const batch = await get('/api/v1/vendor/products', {
        params: { query: { page, size } },
      });
      if (!Array.isArray(batch)) {
        throw new Error('Vendor products response must be an array');
      }

      products.push(...batch);
      if (batch.length < size) return products;
      page += 1;
    }
  },

  getProduct: (productId) =>
      get('/api/v1/vendor/products/{product_id}', {
        params: { path: { product_id: productId } },
      }),

  createProduct: async (body) => {
    const { data, error } = await apiClient.POST('/api/v1/vendor/products', { body });
    if (error) throw error;
    return data;
  },

  updateProduct: async (productId, body) => {
    const { data, error } = await apiClient.PATCH(
        '/api/v1/vendor/products/{product_id}',
        { params: { path: { product_id: productId } }, body },
    );
    if (error) throw error;
    return data;
  },

  updateProductCategories: async (productId, categoryIds) => {
    const { data, error } = await apiClient.PATCH(
        '/api/v1/vendor/products/{product_id}/categories',
        { params: { path: { product_id: productId } }, body: categoryIds },
    );
    if (error) throw error;
    return data;
  },

  deleteProduct: async (productId) => {
    const { error } = await apiClient.DELETE('/api/v1/vendor/products/{product_id}', {
      params: { path: { product_id: productId } },
    });
    if (error) throw error;
  },

  uploadImage: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const { data, error } = await apiClient.POST('/api/v1/vendor/products/upload-image', {
      body: formData,
    });
    if (error) throw error;
    return data;
  },

  // Product categories
  getCategories: () => get('/api/v1/vendor/products/categories'),

  getCategory: (categoryId) =>
      get('/api/v1/vendor/products/categories/{category_id}', {
        params: { path: { category_id: categoryId } },
      }),

  createCategory: async (body) => {
    const { data, error } = await apiClient.POST('/api/v1/vendor/products/categories', { body });
    if (error) throw error;
    return data;
  },

  updateCategory: async (categoryId, body) => {
    const { data, error } = await apiClient.PATCH(
        '/api/v1/vendor/products/categories/{category_id}',
        { params: { path: { category_id: categoryId } }, body },
    );
    if (error) throw error;
    return data;
  },

  deleteCategory: async (categoryId) => {
    const { error } = await apiClient.DELETE(
        '/api/v1/vendor/products/categories/{category_id}',
        { params: { path: { category_id: categoryId } } },
    );
    if (error) throw error;
  },

  // Orders
  getOrders: (params) => get('/api/v1/vendor/orders', { params: { query: params } }),

  getOrder: (orderId) =>
      get('/api/v1/vendor/orders/{order_id}', {
        params: { path: { order_id: orderId } },
      }),

  decideCheques: async (orderId, decision) => {
    const { data, error } = await apiClient.POST(
      '/api/v1/vendor/orders/{order_id}/cheques/decision',
      { params: { path: { order_id: orderId } }, body: { decision } },
    );
    if (error) throw error;
    return data;
  },

  updateOrderStatus: async (orderId, newStatus) => {
    const { data, error } = await apiClient.PATCH(
        '/api/v1/vendor/orders/{order_id}/status',
        {
          params: { path: { order_id: orderId }, query: { new_status: newStatus } },
        },
    );
    if (error) throw error;
    return data;
  },

  respondToOrder: async (orderId, status) => {
    const { data, error } = await apiClient.PATCH(
        '/api/v1/vendor/orders/{order_id}/respond',
        { params: { path: { order_id: orderId } }, body: { status } },
    );
    if (error) throw error;
    return data;
  },

  shipOrder: async (orderId, trackingNumber) => {
    const { data, error } = await apiClient.PATCH(
        '/api/v1/vendor/orders/{order_id}/ship',
        { params: { path: { order_id: orderId } }, body: { tracking_number: trackingNumber } },
    );
    if (error) throw error;
    return data;
  },

  deliverOrder: async (orderId) => {
    const { data, error } = await apiClient.PATCH(
        '/api/v1/vendor/orders/{order_id}/deliver',
        { params: { path: { order_id: orderId } } },
    );
    if (error) throw error;
    return data;
  },

  pack: async (orderId, body = {}) => {
    const { data, error } = await apiClient.PATCH(
      '/api/v1/vendor/orders/{order_id}/pack',
      { params: { path: { order_id: orderId } }, body },
    );
    if (error) throw error;
    return data;
  },

  handover: async (orderId, body) => {
    const { data, error } = await apiClient.PATCH(
      '/api/v1/vendor/orders/{order_id}/handover',
      { params: { path: { order_id: orderId } }, body },
    );
    if (error) throw error;
    return data;
  },

  openPackingBatch: async (body = {}) => {
    const { data, error } = await apiClient.POST(
      '/api/v1/vendor/packing-batches',
      { body },
    );
    if (error) throw error;
    return data;
  },

  addPackingBatchItems: async (batchId, items) => {
    const { data, error } = await apiClient.POST(
      '/api/v1/vendor/packing-batches/{batch_id}/items',
      { params: { path: { batch_id: batchId } }, body: { items } },
    );
    if (error) throw error;
    return data;
  },

  closePackingBatch: async (batchId) => {
    const { data, error } = await apiClient.POST(
      '/api/v1/vendor/packing-batches/{batch_id}/close',
      { params: { path: { batch_id: batchId } } },
    );
    if (error) throw error;
    return data;
  },

  // 👇 Simulation Products (اضافه شده برای شبیه ساز سبد خرید)
  getSimulatorProducts: () => get('/api/v1/vendor/simulation-products'),

  bulkUpdateSimulatorProducts: async (items) => {
    const { data, error } = await apiClient.PUT('/api/v1/vendor/simulation-products/bulk', {
      body: items
    });
    if (error) throw error;
    return data;
  },

  // Packages
  getPackages: () => get('/api/v1/vendor/packages'),
  createPackage: async (body) => {
    const { data, error } = await apiClient.POST('/api/v1/vendor/packages', { body });
    if (error) throw error;
    return data;
  },
  updatePackage: async (packageId, body) => {
    const { data, error } = await apiClient.PUT('/api/v1/vendor/packages/{package_id}', {
      params: { path: { package_id: packageId } },
      body
    });
    if (error) throw error;
    return data;
  },
  deletePackage: async (packageId) => {
    const { error } = await apiClient.DELETE('/api/v1/vendor/packages/{package_id}', {
      params: { path: { package_id: packageId } },
    });
    if (error) throw error;
  },

  // Settings
  updateB2BSettings: async (isEnabled) => {
    // 👈 تغییر آدرس به اندپوینت اختصاصی
    const { data, error } = await apiClient.PATCH('/api/v1/vendor/profile/b2b-settings', {
      body: { is_b2b_enabled: isEnabled }
    });
    if (error) throw error;
    return data;
  },
};